/**
 * Cosumy Gift Manager (v2)
 * --------------------------------------------------------------------------
 * Auto-adds/removes Milchkännchen (SKU 7770201) at 79 € non-gift subtotal.
 * Pricing: Shopify automatic discount "Milchkanne 79 Euro" (no line properties).
 *
 * Cart attributes:
 *   _gift_auto_added         – manager owns the gift line
 *   _gift_dismissed_at_total – non-gift cents when customer dismissed gift
 */
(function () {
  'use strict';

  var GIFT_SKU = '7770201';
  var GIFT_THRESHOLD_CENTS = 7900;
  var AUTO_ADDED_ATTR = '_gift_auto_added';
  var DISMISS_ATTR = '_gift_dismissed_at_total';
  var VARIANT_CACHE_KEY = 'cosumy_gift_variant_id_v2';
  var DEBOUNCE_MS = 280;

  var giftVariantId = null;
  var isReconciling = false;
  var reconcileTimer = null;
  var inflightCart = null;
  var pendingUserCartWrites = 0;
  var queuedReconcile = false;

  function loadCachedVariantId() {
    try {
      var raw = localStorage.getItem(VARIANT_CACHE_KEY);
      if (!raw) return null;
      var id = parseInt(raw, 10);
      return Number.isFinite(id) ? id : null;
    } catch (e) {
      return null;
    }
  }

  function saveCachedVariantId(id) {
    try {
      localStorage.setItem(VARIANT_CACHE_KEY, String(id));
    } catch (e) { /* noop */ }
  }

  async function findGiftVariant() {
    var cached = loadCachedVariantId();
    if (cached) return cached;
    for (var attempt = 0; attempt < 3; attempt++) {
      try {
        var res = await fetch('/products.json?limit=250');
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var data = await res.json();
        var products = (data && data.products) || [];
        for (var i = 0; i < products.length; i++) {
          var variants = products[i].variants || [];
          for (var j = 0; j < variants.length; j++) {
            if (variants[j].sku === GIFT_SKU && variants[j].available) {
              saveCachedVariantId(variants[j].id);
              return variants[j].id;
            }
          }
        }
        return null;
      } catch (e) {
        await new Promise(function (r) { setTimeout(r, 400 * (attempt + 1)); });
      }
    }
    return null;
  }

  function getCart() {
    if (inflightCart) return inflightCart;
    inflightCart = fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .finally(function () { inflightCart = null; });
    return inflightCart;
  }

  function isDiscountedGiftLine(item) {
    if (!item || item.variant_id !== giftVariantId) return false;
    var price = item.final_line_price !== undefined ? item.final_line_price : item.final_price;
    if (price === undefined) return false;
    return Number(price) === 0;
  }

  function getVariantLineEntries(cart) {
    var entries = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === giftVariantId) {
        entries.push({ item: items[i], line: i + 1 });
      }
    }
    return entries;
  }

  function findGiftLine(cart) {
    var entries = getVariantLineEntries(cart);
    for (var i = 0; i < entries.length; i++) {
      if (isDiscountedGiftLine(entries[i].item)) return entries[i].item;
    }
    return null;
  }

  function calcNonGiftTotal(cart) {
    var total = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (item.variant_id === giftVariantId && isDiscountedGiftLine(item)) continue;
      total += item.original_line_price || 0;
    }
    return total;
  }

  function getCartAttr(cart, key) {
    return ((cart && cart.attributes) || {})[key];
  }

  function getDismissedAt(cart) {
    var raw = getCartAttr(cart, DISMISS_ATTR);
    if (raw === undefined || raw === null || raw === '') return null;
    var n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  function shouldPauseReconcile() {
    return pendingUserCartWrites > 0;
  }

  async function waitUntilUserIdle(maxMs) {
    var deadline = Date.now() + (maxMs || 120000);
    while (pendingUserCartWrites > 0 && Date.now() < deadline) {
      await new Promise(function (r) { setTimeout(r, 40); });
    }
    return pendingUserCartWrites === 0;
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  }

  async function setCartAttribute(key, value) {
    await waitUntilUserIdle();
    if (shouldPauseReconcile()) return false;
    var attrs = {};
    attrs[key] = value === null ? '' : String(value);
    var res = await postJson('/cart/update.js', { attributes: attrs });
    return res.ok;
  }

  async function addGiftLine() {
    await waitUntilUserIdle();
    if (shouldPauseReconcile()) return false;
    var res = await postJson('/cart/add.js', { items: [{ id: giftVariantId, quantity: 1 }] });
    return res.ok;
  }

  async function changeLineQty(line, quantity) {
    var res = await postJson('/cart/change.js', { line: line, quantity: quantity });
    return res.ok;
  }

  async function removeGiftLine() {
    await waitUntilUserIdle();
    if (shouldPauseReconcile()) return false;
    var cart = await getCart();
    var entries = getVariantLineEntries(cart).filter(function (e) {
      return isDiscountedGiftLine(e.item);
    });
    if (!entries.length) return true;
    for (var i = entries.length - 1; i >= 0; i--) {
      if (!(await changeLineQty(entries[i].line, 0))) return false;
    }
    return true;
  }

  function giftVariantNeedsRebuild(entries) {
    if (!entries || entries.length <= 1) return false;
    var free = entries.filter(function (e) {
      return isDiscountedGiftLine(e.item);
    });
    var paid = entries.filter(function (e) {
      return !isDiscountedGiftLine(e.item);
    });
    var freeQty = free.reduce(function (a, e) {
      return a + (e.item.quantity || 0);
    }, 0);
    return entries.length > 2 || free.length > 1 || paid.length > 1 || freeQty > 1;
  }

  /**
   * Shopify can split Milchkännchen into several free + paid rows.
   * Rebuild as a single add so the automatic discount reapplies as
   * at most 1 free line + 1 paid line.
   */
  async function normalizeGiftVariantLines(cart) {
    if (!giftVariantId || shouldPauseReconcile()) return false;
    var entries = getVariantLineEntries(cart);
    if (!giftVariantNeedsRebuild(entries)) return false;

    var totalQty = entries.reduce(function (a, e) {
      return a + (e.item.quantity || 0);
    }, 0);
    var zero = {};
    zero[giftVariantId] = 0;
    var z = await postJson('/cart/update.js', { updates: zero });
    if (!z.ok) return false;
    if (totalQty > 0) {
      var a = await postJson('/cart/add.js', {
        items: [{ id: giftVariantId, quantity: totalQty }],
      });
      if (!a.ok) return false;
    }
    return true;
  }

  function hasActiveNonGiftUserIntent() {
    if (
      !window.CosumyLineConsolidator ||
      typeof window.CosumyLineConsolidator.getActiveUserIntent !== 'function'
    ) {
      return false;
    }
    var hasIntent = false;
    document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(function (inp) {
      var vid = inp.dataset.quantityVariantId;
      if (!vid || String(vid) === String(giftVariantId)) return;
      if (window.CosumyLineConsolidator.getActiveUserIntent(vid) !== null) hasIntent = true;
    });
    return hasIntent;
  }

  function getSectionTargets() {
    var targets = [];
    if (document.getElementById('CartDrawer')) {
      targets.push({ sectionId: 'cart-drawer', domId: 'CartDrawer', selector: '#CartDrawer' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ sectionId: 'cart-icon-bubble', domId: 'cart-icon-bubble' });
    }
    if (document.getElementById('cart-live-region-text')) {
      targets.push({ sectionId: 'cart-live-region-text', domId: 'cart-live-region-text' });
    }
    var cartItems = document.getElementById('main-cart-items');
    if (cartItems && cartItems.dataset && cartItems.dataset.id) {
      targets.push({
        sectionId: cartItems.dataset.id,
        domId: 'main-cart-items',
        selector: '#main-cart-items .js-contents',
      });
    }
    var cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter && cartFooter.dataset && cartFooter.dataset.id) {
      targets.push({
        sectionId: cartFooter.dataset.id,
        domId: 'main-cart-footer',
        selector: '#main-cart-footer .js-contents',
      });
    }
    return targets;
  }

  async function refreshSections() {
    try {
      var targets = getSectionTargets();
      if (!targets.length) return;
      var ids = targets.map(function (t) { return t.sectionId; }).join(',');
      var res = await fetch('/?sections=' + encodeURIComponent(ids), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      var data = await res.json();
      targets.forEach(function (t) {
        var html = data[t.sectionId];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        if (t.selector) {
          var src = parsed.querySelector(t.selector);
          var dest = document.querySelector(t.selector);
          if (src && dest) dest.innerHTML = src.innerHTML;
        } else {
          var srcEl = parsed.getElementById(t.domId);
          var destEl = document.getElementById(t.domId);
          if (srcEl && destEl) destEl.innerHTML = srcEl.innerHTML;
        }
      });
    } catch (e) {
      console.warn('[Cosumy Gift] section refresh failed', e);
    }
    if (window.CosumyLineConsolidator) {
      if (typeof window.CosumyLineConsolidator.invalidateServerCache === 'function') {
        window.CosumyLineConsolidator.invalidateServerCache();
      }
      if (typeof window.CosumyLineConsolidator.apply === 'function') {
        window.CosumyLineConsolidator.apply();
      }
    }
  }

  function publishCartUpdated() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cosumy-gift-manager', cartData: null });
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cosumy-gift-manager' } }));
  }

  async function finalizeCartShape() {
    if (shouldPauseReconcile()) return;
    if (hasActiveNonGiftUserIntent()) {
      if (window.CosumyLineConsolidator && typeof window.CosumyLineConsolidator.apply === 'function') {
        await window.CosumyLineConsolidator.apply();
      }
      return;
    }
    if (
      window.CosumyLineConsolidator &&
      typeof window.CosumyLineConsolidator.getActiveUserIntent === 'function'
    ) {
      var hasIntent = false;
      document.querySelectorAll('.quantity__input[data-quantity-variant-id]').forEach(function (inp) {
        if (window.CosumyLineConsolidator.getActiveUserIntent(inp.dataset.quantityVariantId) !== null) {
          hasIntent = true;
        }
      });
      if (hasIntent) {
        if (typeof window.CosumyLineConsolidator.apply === 'function') {
          await window.CosumyLineConsolidator.apply();
        }
        return;
      }
    }
    try {
      var cart = await getCart();
      if (await normalizeGiftVariantLines(cart)) {
        await refreshSections();
        publishCartUpdated();
      }
    } catch (e) { /* noop */ }
    if (window.CosumyLineConsolidator && typeof window.CosumyLineConsolidator.merge === 'function') {
      if (await window.CosumyLineConsolidator.merge()) {
        await refreshSections();
      }
    }
    if (window.CosumyLineConsolidator && typeof window.CosumyLineConsolidator.apply === 'function') {
      await window.CosumyLineConsolidator.apply();
    }
  }

  async function reconcile() {
    if (!giftVariantId) return;
    if (isReconciling) {
      queuedReconcile = true;
      return;
    }
    if (shouldPauseReconcile()) {
      queuedReconcile = true;
      return;
    }

    isReconciling = true;
    var didMutate = false;
    try {
      if (!(await waitUntilUserIdle())) {
        queuedReconcile = true;
        return;
      }

      var cart = await getCart();
      var items = (cart && cart.items) || [];
      var giftLine = findGiftLine(cart);
      var nonGiftTotal = calcNonGiftTotal(cart);
      var dismissedAt = getDismissedAt(cart);
      var wasAutoAdded = String(getCartAttr(cart, AUTO_ADDED_ATTR) || '') === 'true';

      if (items.length === 0) {
        if (wasAutoAdded || dismissedAt !== null) {
          if (shouldPauseReconcile()) { queuedReconcile = true; return; }
          await setCartAttribute(AUTO_ADDED_ATTR, '');
          await setCartAttribute(DISMISS_ATTR, '');
          didMutate = true;
        }
        return;
      }

      if (nonGiftTotal < GIFT_THRESHOLD_CENTS) {
        if (giftLine && wasAutoAdded) {
          if (shouldPauseReconcile()) { queuedReconcile = true; return; }
          if (await removeGiftLine()) {
            await setCartAttribute(AUTO_ADDED_ATTR, '');
            await refreshSections();
            publishCartUpdated();
            didMutate = true;
          }
        }
        if (await normalizeGiftVariantLines(cart)) {
          await refreshSections();
          publishCartUpdated();
          didMutate = true;
        }
        return;
      }

      if (!giftLine) {
        if (dismissedAt === null || nonGiftTotal > dismissedAt) {
          if (shouldPauseReconcile()) { queuedReconcile = true; return; }
          if (await addGiftLine()) {
            await setCartAttribute(AUTO_ADDED_ATTR, 'true');
            if (dismissedAt !== null) await setCartAttribute(DISMISS_ATTR, '');
            await refreshSections();
            publishCartUpdated();
            didMutate = true;
          }
        }
      }

      cart = await getCart();
      if (!hasActiveNonGiftUserIntent() && (await normalizeGiftVariantLines(cart))) {
        await refreshSections();
        publishCartUpdated();
        didMutate = true;
      }
    } catch (e) {
      console.error('[Cosumy Gift] reconcile error', e);
    } finally {
      try {
        if (didMutate) {
          await finalizeCartShape();
        } else if (window.CosumyLineConsolidator && typeof window.CosumyLineConsolidator.apply === 'function') {
          await window.CosumyLineConsolidator.apply();
        }
      } catch (e2) {
        console.warn('[Cosumy Gift] finalizeCartShape error', e2);
      }
      isReconciling = false;
      if (queuedReconcile) {
        queuedReconcile = false;
        scheduleReconcile();
      }
    }
  }

  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      if (shouldPauseReconcile()) {
        scheduleReconcile();
        return;
      }
      reconcile();
    }, DEBOUNCE_MS);
  }

  function beginUserCartWrite() {
    pendingUserCartWrites++;
  }

  function endUserCartWrite() {
    pendingUserCartWrites = Math.max(0, pendingUserCartWrites - 1);
    scheduleReconcile();
  }

  async function dismissGift() {
    if (!giftVariantId) return;
    while (isReconciling) {
      await new Promise(function (r) { setTimeout(r, 50); });
    }
    isReconciling = true;
    try {
      await waitUntilUserIdle();
      var cart = await getCart();
      var nonGiftTotal = calcNonGiftTotal(cart);
      await setCartAttribute(DISMISS_ATTR, String(nonGiftTotal));
      await setCartAttribute(AUTO_ADDED_ATTR, '');
      await removeGiftLine();
      await refreshSections();
      publishCartUpdated();
      await finalizeCartShape();
    } catch (e) {
      console.error('[Cosumy Gift] dismiss error', e);
    } finally {
      isReconciling = false;
    }
  }

  document.addEventListener(
    'click',
    function (e) {
      var trigger = e.target.closest('[data-gift-dismiss="true"]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      dismissGift();
    },
    true
  );

  function trySubscribe() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'cosumy-gift-manager') return;
        scheduleReconcile();
      });
      return true;
    }
    return false;
  }
  if (!trySubscribe()) {
    var tries = 0;
    var t = setInterval(function () {
      if (trySubscribe() || ++tries > 30) clearInterval(t);
    }, 200);
  }

  document.addEventListener('cart:updated', function (e) {
    if (e && e.detail && e.detail.source === 'cosumy-gift-manager') return;
    scheduleReconcile();
  });
  document.addEventListener('cart:change', scheduleReconcile);

  window.CosumyGiftManager = {
    dismiss: dismissGift,
    reconcile: scheduleReconcile,
    reconcileNow: reconcile,
    beginUserCartWrite: beginUserCartWrite,
    endUserCartWrite: endUserCartWrite,
    hasPendingWrites: function () { return pendingUserCartWrites > 0; },
    isReconciling: function () { return isReconciling; },
    clearVariantCache: function () {
      try { localStorage.removeItem(VARIANT_CACHE_KEY); } catch (e) { /* noop */ }
    },
  };

  async function boot() {
    giftVariantId = await findGiftVariant();
    if (!giftVariantId) {
      console.warn('[Cosumy Gift] gift variant SKU ' + GIFT_SKU + ' not found – manager disabled');
      return;
    }
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
